import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useParcelDataEnrichment, useTaskPolling } from "../../hooks";
import type { MpzpOrder } from "../../types";

/**
 * Przeliczenie brakujących danych działek WPROST ZE STRONY ZAMÓWIENIA.
 *
 * Braki widać dopiero, gdy patrzy się na gotowy wniosek — odsyłanie użytkownika
 * z powrotem na listę działek po to, żeby kliknął ten sam przycisk, jest tylko
 * podróżą. Zadania są te same i nadal per projekt: zamówienie może obejmować
 * działki z kilku projektów, więc odpalamy je dla każdego z nich.
 */
export function MpzpOrderDataFixBar({ order }: { order: MpzpOrder }) {
  const enrich = useParcelDataEnrichment();
  const [taskIds, setTaskIds] = useState<string[]>([]);
  const [settled, setSettled] = useState(0);
  const [error, setError] = useState("");

  const running = taskIds.length > 0 && settled < taskIds.length;

  const run = async () => {
    if (running) return;
    setError("");
    // Zadania są per PROJEKT, a zamówienie chodzi po gminie i bywa wieloprojektowe —
    // backend podaje listę projektów jego działek.
    const ids = order.connection_ids ?? [];
    if (!ids.length) {
      setError("Nie udało się ustalić projektów tego zamówienia.");
      return;
    }
    const started = await Promise.all(
      ids.flatMap((id) => [
        enrich.adminData.mutateAsync(id).catch(() => null),
        enrich.streets.mutateAsync({ connectionId: id }).catch(() => null),
      ]),
    );
    const ok = started.filter(Boolean).map((r) => r!.task_id);
    setSettled(0);
    setTaskIds(ok);
    if (!ok.length) setError("Nie udało się uruchomić przeliczenia.");
  };

  const done = taskIds.length > 0 && settled >= taskIds.length;

  return (
    <div className="mt-2 space-y-1.5">
      <Button size="sm" variant="secondary" loading={running} disabled={running} onClick={run}>
        {running ? "Przeliczam…" : "Uzupełnij dane działek"}
      </Button>

      {/* Zadania w tle bywają szybkie, ale nie natychmiastowe — bez widocznego
          wskaźnika klik wygląda, jakby nic się nie stało. */}
      {running && (
        <div className="flex items-center gap-2 text-[11px] text-amber-900">
          <Spinner size="sm" />
          <span>
            Przeliczam dane działek… ukończono {settled} z {taskIds.length} zadań
          </span>
        </div>
      )}
      {done && (
        <div className="text-[11px] text-green-700">
          Przeliczono. Wygeneruj wniosek ponownie, żeby dane weszły do dokumentu.
        </div>
      )}

      <div className="text-[10px] text-amber-900/80">
        Odpytuje rejestr działek (gmina, obręb) i geokoder (miejscowość, adres) dla
        projektów tego zamówienia.
      </div>
      {taskIds.map((id) => (
        <TaskWatch key={id} taskId={id} onSettled={() => setSettled((n) => n + 1)} />
      ))}
      {error && <div className="text-[11px] text-red-700">{error}</div>}
    </div>
  );
}

/** Pojedyncze zadanie — własny komponent, bo useTaskPolling to hook. */
function TaskWatch({ taskId, onSettled }: { taskId: string; onSettled: () => void }) {
  const { data } = useTaskPolling(taskId);
  const status = data?.status;

  useEffect(() => {
    if (status && ["success", "failure", "cancelled"].includes(status)) onSettled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (!status || ["success", "failure", "cancelled"].includes(status)) return null;
  return (
    <div className="text-[10px] font-mono text-amber-900/80">
      {(data?.log ?? "").trim().split("\n").slice(-1)[0] || "pracuję…"}
    </div>
  );
}
